import { useQuery, UseQueryResult } from 'react-query';
import { getEnterprisesByName } from '../../common/src/legacy/services/apiRouterService';
import { ConvoHistorySessions } from '../../../types/ConversationHistory';

import { getLegalPortfolioForDistribution } from '../services/apiService';
import { CbeLookupResult, DistributionPortfolio } from '../types/apiTypes';
import useCompanyScanFlowId from './useCompanyScanFlowId';
import useAuth from '../../app/src/features/authentication/hooks/useAuth';
import { getConvoHistoryAll } from '../../conversations/src/features/overview/api/legacy/legacyApi';
import { isSocialLoginUrl } from '../../app/src/features/authentication/constants/SocialAuth';

export const usePortfolioCompanies = (
  companyName?: string,
): UseQueryResult<DistributionPortfolio> => {
  const { distribution } = useAuth();

  return useQuery<DistributionPortfolio>(
    ['company-scan', 'legal-callant', companyName],
    async () => {
      const [resp, status] = await getLegalPortfolioForDistribution(
        distribution?.id || '',
        companyName,
      );
      if (status >= 400) throw new Error();
      return resp;
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: Boolean(
        companyName?.length && companyName.length > 1 && !isSocialLoginUrl,
      ),
    },
  );
};

export const useCompanyLookup = (
  nameOrCbe?: string,
  onSuccess?: (company: CbeLookupResult) => void,
): UseQueryResult<CbeLookupResult> => {
  return useQuery<CbeLookupResult, unknown, CbeLookupResult>(
    ['company-scan', 'companies', nameOrCbe],
    async () => {
      const [resp, status] = await getEnterprisesByName(nameOrCbe);
      if (status >= 400) {
        if (
          (resp.detail as string).match(/No enterprise is found with given.*/)
        ) {
          return { items: [] };
        }

        throw new Error();
      }
      return resp;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: Boolean(nameOrCbe?.length && nameOrCbe.length > 1),
      onSuccess,
    },
  );
};

export const useCompanyConversations = (
  partyId?: string,
  isCompleted?: boolean,
): UseQueryResult<ConvoHistorySessions> => {
  const companyScanFlowId = useCompanyScanFlowId();

  return useQuery(
    ['company-scan', 'company-sessions', partyId, isCompleted],
    async () => {
      const [resp] = await getConvoHistoryAll({
        party_id: [partyId || ''],
        flow_id: companyScanFlowId,
        ...(isCompleted && { status: 'COMPLETED' }),
      });
      return resp;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: !!partyId,
    },
  );
};
